// Per-route rate-limit hits over the rolling 1h window. Fans-out across
// Studio + Builder for the currently-selected env via /api/admin/rate-limits.
//
// CAVEAT: counters are in-memory per Vercel function instance and reset on
// cold start. A high-traffic env may have multiple instances; this view
// shows ONE instance's snapshot, not aggregate platform truth. Treat the
// numbers as a sales-ops signal, not a billing-grade audit.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

type AdminEnv = 'dev' | 'staging' | 'prod';
type AppKey = 'studio' | 'builder';

interface PerRouteEntry {
  category: string;
  hits: number;
  throttled: number;
  throttleRate: number;
}

interface PerAppResult {
  app: AppKey;
  url: string;
  ok: boolean;
  perRoute: PerRouteEntry[];
  instanceId?: string;
  asOf?: string;
  error: string | null;
}

interface RateLimitsPayload {
  env: AdminEnv;
  fetchedAt: string;
  windowMs: number;
  perApp: PerAppResult[];
  warning?: string;
}

const ENV_LABEL: Record<AdminEnv, string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function RateLimitHitsBlock({ env }: { env: AdminEnv }) {
  const [data, setData] = useState<RateLimitsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/admin/rate-limits?env=${env}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const body = (await res.json()) as RateLimitsPayload;
      setData(body);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load rate-limit stats');
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  // Flatten across apps so we can render one combined table.
  const rows: Array<PerRouteEntry & { app: AppKey }> = [];
  for (const a of data?.perApp ?? []) {
    if (!a.ok) continue;
    for (const r of a.perRoute) {
      rows.push({ ...r, app: a.app });
    }
  }
  // Sort by hits desc, ties broken by category asc.
  rows.sort((a, b) => b.hits - a.hits || a.category.localeCompare(b.category));

  const anyDown = (data?.perApp ?? []).some((a) => !a.ok);
  const allEmpty = rows.length === 0;

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
            Rate-limit hits (rolling 1h · {ENV_LABEL[env]})
          </h3>
          {data?.fetchedAt && (
            <span className="text-[10px] text-slate-500 font-mono">
              fetched {formatRelative(data.fetchedAt)}
            </span>
          )}
          {data?.perApp?.[0]?.instanceId && (
            <span
              className="text-[10px] text-slate-500 font-mono"
              title="In-memory counters are per Vercel function instance; this is one instance's snapshot, not aggregate platform truth."
            >
              · instance: {data.perApp[0].instanceId}
            </span>
          )}
        </div>
        <button
          onClick={() => void fetchStats()}
          disabled={loading}
          className="text-[10px] uppercase tracking-wider font-bold text-indigo-300 hover:text-indigo-200 disabled:text-slate-600 disabled:cursor-not-allowed"
        >
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 text-[11px] text-rose-300 bg-rose-950/20 border-b border-rose-900/30">
          {error}
        </div>
      )}
      {data?.warning && (
        <div className="px-4 py-2 text-[10px] text-amber-200 bg-amber-950/20 border-b border-amber-900/30">
          {data.warning}
        </div>
      )}

      {/* Per-app status row: which apps responded, which couldn't be reached. */}
      {data && (
        <div className="px-4 py-2 border-b border-slate-800 flex items-center gap-3 flex-wrap text-[10px]">
          {data.perApp.map((a) => (
            <span
              key={a.app}
              className={`px-1.5 py-0.5 rounded border font-mono uppercase tracking-wider font-bold ${
                a.ok
                  ? 'text-emerald-300 border-emerald-700/40 bg-emerald-950/30'
                  : 'text-rose-300 border-rose-700/40 bg-rose-950/30'
              }`}
              title={a.ok ? a.url : (a.error ?? 'fetch failed')}
            >
              {a.app}: {a.ok ? 'ok' : 'down'}
            </span>
          ))}
          {anyDown && (
            <span className="text-slate-500">
              · one or more apps unreachable — partial results
            </span>
          )}
        </div>
      )}

      {allEmpty && !loading && !error ? (
        <div className="px-4 py-6 text-center text-[11px] text-slate-500 italic">
          No rate-limit checks recorded in the last hour on the responding instance(s).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">App</th>
                <th className="px-3 py-2 text-right">Hits</th>
                <th className="px-3 py-2 text-right">Throttled</th>
                <th className="px-3 py-2 text-right">Throttle rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => {
                const tone = throttleTone(r.throttleRate);
                return (
                  <tr
                    key={`${r.app}:${r.category}`}
                    className={`${tone.row} hover:bg-slate-900/40`}
                  >
                    <td className="px-3 py-2 font-mono text-slate-200">{r.category}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold border ${
                          r.app === 'studio'
                            ? 'bg-amber-900/30 text-amber-300 border-amber-700/40'
                            : 'bg-indigo-900/30 text-indigo-300 border-indigo-700/40'
                        }`}
                      >
                        {r.app === 'studio' ? 'PS' : 'PB'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300 font-mono">
                      {r.hits.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={r.throttled > 0 ? tone.text : 'text-slate-500'}>
                        {r.throttled.toLocaleString()}
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${tone.text}`}>
                      {(r.throttleRate * 100).toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function throttleTone(rate: number): { row: string; text: string } {
  // ≥15% red, ≥5% amber, else slate.
  if (rate >= 0.15) {
    return { row: 'bg-rose-950/10', text: 'text-rose-300' };
  }
  if (rate >= 0.05) {
    return { row: 'bg-amber-950/10', text: 'text-amber-300' };
  }
  return { row: '', text: 'text-slate-300' };
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}
